<template>
    <div class="flex flex-column mb-3">
        <b-alert v-if="success" variant="success" show>
            <font-awesome-icon :icon="['far', 'square-check']" size="xl" class="mr-2"></font-awesome-icon>
            {{ success }}
        </b-alert>
        <b-alert v-if="error" variant="error" show>
            <font-awesome-icon :icon="['far', 'square-exclamation']" size="xl" class="mr-2"></font-awesome-icon>
            {{ error }}
        </b-alert>
        <p>{{ $t('admin.admin_categories') }}</p>
        <div v-if="loadingSigns" class="text-center mt-5 pt-4">
            <b-spinner class="text-highlight"></b-spinner>
        </div>
        <div v-else-if="categories.length === 0" class="text-center mt-5">
            <p>{{ $t('admin.no_categories_available') }}</p>
        </div>
        <div v-else class="d-flex flex-column flex-wrap justify-content-center align-items-start mt-3 gap-3">
            <Button
                class="mt-3 mb-3"
                @click="openCategoryModal()"
                :icon="['fal', 'plus']"
                :label="$t('admin.create_category')"
            />
            <ul class="w-100">
                <AdminItem
                    v-for="category in categories"
                    :key="category.id"
                    :icon="category.icon"
                    :id="category.id"
                    :name="category.name"
                    :signBankIds="category.signBankIds"
                    :signBankId="category.signBankId"
                    @edit-item="openCategoryModalWithName(category.id)"
                    @delete-item="deleteConfirmation(category.id)"
                />
            </ul>
        </div>
        <CategoryModal :category="selectedCategory" @submit="onCompletion" />
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import SvgIcon from '@/components/SvgIcon.vue';
import Button from '@/components/general/Button.vue';
import CategoryModal from '@/components/admin/CategoryModal.vue';
import { ICategory } from '../../../../functions/src/shared/contracts/api/MasterdataContract';
import { Constants } from '@/shared/Constants';
import { axiosHttp } from '@/util/AxiosHttp';
import AdminItem from './AdminItem.vue';

const cacheModule = namespace('cache');

@Component({ components: { SvgIcon, Button, CategoryModal, AdminItem } })
export default class CategoriesTab extends Vue {
    @cacheModule.Getter('categories')
    private categories: ICategory[];

    public selectedCategory: ICategory | null = null;
    public error: string | null = null;
    public success: string | null = null;
    public loadingSigns: boolean = false;

    public openCategoryModal(): void {
        this.selectedCategory = null;
        this.$bvModal.show('categoryModal');
    }

    public openCategoryModalWithName(categoryName: string): void {
        this.selectedCategory = this.categories.find((category) => category.id === categoryName) || null;
        this.$bvModal.show('categoryModal');
    }

    public deleteConfirmation(categoryName: string): void {
        this.selectedCategory = this.categories.find((category) => category.id === categoryName) || null;
        const selectedCategoryId = this.selectedCategory.id;
        this.$bvModal
            .msgBoxConfirm(this.$t('admin.delete_category_confirmation', { selectedCategoryId }).toString(), {
                title: this.$t('admin.delete_category_title').toString(),
                size: 'md',
                buttonSize: 'md',
                centered: true,
                okVariant: 'danger',
                okTitle: this.$t('admin.delete').toString(),
                cancelVariant: 'secondary',
                cancelTitle: this.$t('general.cancel').toString(),
            })
            .then((confirmed) => {
                if (confirmed) {
                    this.deleteCategory(selectedCategoryId);
                }
            });
    }

    private async deleteCategory(categoryName: string): Promise<void> {
        try {
            this.error = '';
            await axiosHttp.http.delete<ICategory>(`${Constants.baseOperationsUrl}/categories/${categoryName}`);
            this.success = this.$t('admin.category_deleted').toString();
            await this.handleRefresh();
        } catch (error) {
            this.success = '';
            this.error = `${this.$t('admin.category_deletion_error')}: ${error}`;
        }
    }

    @cacheModule.Action
    private refreshCategories!: () => Promise<void>;

    private async handleRefresh() {
        this.loadingSigns = true;
        try {
            await this.refreshCategories();
        } catch (error) {
            this.success = '';
            this.error = `${this.$t('admin.category_loading_error')}`;
        } finally {
            this.loadingSigns = false;
        }
    }

    public async onCompletion(): Promise<void> {
        this.success = this.selectedCategory
            ? this.$t('admin.category_updated').toString()
            : this.$t('admin.category_created').toString();
        this.handleRefresh;
    }
}
</script>
